import * as React from 'react';

export interface IProductCollectionCarouselProps {
    items: React.ReactNode[];
    itemsPerView: number;
    compactView?: boolean;
    ariaLabel?: string;
    autoplay?: boolean;
    interval?: number;
    pauseOnHover?: boolean;
}

/** A native-scroll carousel with scroll snapping and accessible page controls. */
const ProductCollectionCarousel: React.FC<IProductCollectionCarouselProps> = ({
    items,
    itemsPerView,
    compactView = false,
    ariaLabel = 'Product carousel',
    autoplay = false,
    interval = 10000,
    pauseOnHover = true
}) => {
    const baseClassName = 'dlvry-product-collection-carousel';
    const trackRef = React.useRef<HTMLDivElement>(null);
    const [canScrollPrevious, setCanScrollPrevious] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const [isHovering, setIsHovering] = React.useState(false);
    const [activePage, setActivePage] = React.useState(0);
    const [effectiveItemsPerView, setEffectiveItemsPerView] = React.useState(itemsPerView);
    const pageCount = Math.max(1, Math.ceil(items.length / effectiveItemsPerView));

    React.useEffect(() => {
        const updateItemsPerView = () => {
            if (!compactView) {
                setEffectiveItemsPerView(itemsPerView);
            } else if (window.innerWidth <= 520) {
                setEffectiveItemsPerView(2);
            } else if (window.innerWidth <= 992) {
                setEffectiveItemsPerView(3);
            } else {
                setEffectiveItemsPerView(itemsPerView);
            }
        };

        updateItemsPerView();
        window.addEventListener('resize', updateItemsPerView);
        return () => window.removeEventListener('resize', updateItemsPerView);
    }, [compactView, itemsPerView]);

    const updateNavigation = React.useCallback(() => {
        const track = trackRef.current;
        if (!track) {
            return;
        }

        const remainingScroll = track.scrollWidth - track.clientWidth - track.scrollLeft;
        setCanScrollPrevious(track.scrollLeft > 1);
        setCanScrollNext(remainingScroll > 1);
        setActivePage(Math.min(pageCount - 1, Math.ceil(track.scrollLeft / track.clientWidth)));
    }, [pageCount]);

    React.useEffect(() => {
        const track = trackRef.current;
        if (!track) {
            return;
        }

        updateNavigation();
        const resizeObserver = new ResizeObserver(updateNavigation);
        resizeObserver.observe(track);

        return () => resizeObserver.disconnect();
    }, [items.length, updateNavigation]);

    const scrollPage = (direction: 1 | -1) => {
        const track = trackRef.current;
        track?.scrollBy({ behavior: 'smooth', left: direction * track.clientWidth });
    };

    const scrollToPage = (page: number) => {
        trackRef.current?.scrollTo({ behavior: 'smooth', left: page * trackRef.current.clientWidth });
    };

    const advancePage = React.useCallback(() => {
        const track = trackRef.current;
        if (!track) {
            return;
        }

        const lastPosition = track.scrollWidth - track.clientWidth;
        const isAtLastPosition = track.scrollLeft >= lastPosition - 1;
        track.scrollTo({
            behavior: 'smooth',
            left: isAtLastPosition ? 0 : Math.min(track.scrollLeft + track.clientWidth, lastPosition)
        });
    }, []);

    React.useEffect(() => {
        if (!autoplay || !interval || (pauseOnHover && isHovering) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        const timer = window.setInterval(advancePage, interval);
        return () => window.clearInterval(timer);
    }, [advancePage, autoplay, interval, isHovering, pauseOnHover]);

    return (
        <section
            className={baseClassName}
            aria-label={ariaLabel}
            style={{ '--items-per-view': itemsPerView } as React.CSSProperties}
            onMouseEnter={() => pauseOnHover && setIsHovering(true)}
            onMouseLeave={() => pauseOnHover && setIsHovering(false)}
        >
            <div
                className={`${baseClassName}__track`}
                ref={trackRef}
                onScroll={updateNavigation}
                tabIndex={0}
            >
                {items}
            </div>
            {pageCount > 1 && (
                <div className={`${baseClassName}__footer`}>
                    <ol className={`${baseClassName}__indicators`} aria-label='Carousel pages'>
                        {Array.from({ length: pageCount }, (_, page) => (
                            <li key={page}>
                                <button
                                    type='button'
                                    aria-label={`Show product page ${page + 1}`}
                                    aria-current={page === activePage ? 'true' : undefined}
                                    className={page === activePage ? 'active' : undefined}
                                    onClick={() => scrollToPage(page)}
                                />
                            </li>
                        ))}
                    </ol>
                    <div className={`${baseClassName}__navigation`}>
                        <button
                            className={`${baseClassName}__button`}
                            type='button'
                            aria-label='Show previous products'
                            disabled={!canScrollPrevious}
                            onClick={() => scrollPage(-1)}
                        >
                            <span aria-hidden='true'>‹</span>
                        </button>
                        <button
                            className={`${baseClassName}__button`}
                            type='button'
                            aria-label='Show next products'
                            disabled={!canScrollNext}
                            onClick={() => scrollPage(1)}
                        >
                            <span aria-hidden='true'>›</span>
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};

export default ProductCollectionCarousel;
